import React, { useState, useEffect, useCallback } from 'react';
import '../shared.css';
import Sidebar from '../Components/Layout/sidebar';
import Topbar from '../Components/Layout/topbar';
import Button from '../Components/Comman/Button';
import Analytic_cards from '../Components/Comman/Analytics_card';
import Modal from '../Components/Comman/Modal';
import {
  FieldGroup, TextInput, FormGrid, FormActions, DeleteConfirm,
} from '../Components/Comman/FormFields';
import { supabase } from '../supabaseClient';

/* Actual DB columns: id, title, benefits, ticket_price, ticket_date, issued_at */

const blank = () => ({
  title: '', benefits: '', ticket_price: 0, ticket_date: '',
});

/* ── Card ── */
const TicketCard = ({ title, benefits, ticket_price, ticket_date, onEdit, onDelete }) => {
  const dateLabel = ticket_date
    ? new Date(ticket_date).toLocaleDateString('en-GB', { dateStyle: 'medium' })
    : '—';

  return (
    <div style={{
      background: 'rgba(214,184,147,0.63)', borderRadius: 10, padding: '22px 24px',
      display: 'flex', flexDirection: 'column', gap: 14, flex: '1 1 280px',
      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h4 style={{ fontFamily: '"Cormorant Garamond"', fontSize: 20, fontWeight: 600, color: '#191919' }}>{title}</h4>
      </div>

      <div>
        <p style={{ fontFamily: '"Cormorant Garamond"', fontSize: 28, fontWeight: 700, color: '#191919' }}>
          {Number(ticket_price) === 0 ? 'Free' : `EGP ${Number(ticket_price).toLocaleString()}`}
        </p>
        <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#8B8B7A', marginTop: 2 }}>per ticket</p>
      </div>

      {benefits && (
        <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#555', lineHeight: 1.5 }}>{benefits}</p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
          <rect x="3" y="4" width="14" height="14" rx="2" stroke="#8B8B7A" strokeWidth="1.6"/>
          <path d="M7 2v3M13 2v3M3 9h14" stroke="#8B8B7A" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
        <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#8B8B7A' }}>{dateLabel}</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="button" onClick={onEdit}
          style={{ flex: 1, background: '#0D0D0D', color: '#D6B893', border: 'none', borderRadius: 5, padding: '7px 0', fontFamily: 'Inter', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#1a1a1a'}
          onMouseLeave={e => e.currentTarget.style.background = '#0D0D0D'}
        >Edit</button>
        <button type="button" onClick={onDelete}
          style={{ flex: 1, background: 'rgba(180,60,60,0.08)', border: '0.762px solid rgba(180,60,60,0.2)', borderRadius: 5, padding: '7px 0', fontFamily: 'Inter', fontSize: 11.5, cursor: 'pointer', color: '#a33', transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(180,60,60,0.15)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(180,60,60,0.08)'}
        >Delete</button>
      </div>
    </div>
  );
};

/* ── Form ── */
const TicketForm = ({ initial, onSave, onCancel, loading }) => {
  const [form, setForm] = useState(initial || blank());
  useEffect(() => { setForm(initial || blank()); }, [initial]);
  const set = (key, asNumber = false) => (val) =>
    setForm(f => ({ ...f, [key]: asNumber ? Number(val) : val }));
  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };

  const dateValue = form.ticket_date ? form.ticket_date.slice(0, 10) : '';

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FormGrid>
          <FieldGroup label="Ticket Title" required>
            <TextInput value={form.title} onChange={set('title')} placeholder="e.g. VIP" required />
          </FieldGroup>
          <FieldGroup label="Price (EGP)" required>
            <TextInput value={form.ticket_price} onChange={set('ticket_price', true)} type="number" placeholder="0 = Free" required />
          </FieldGroup>
          <FieldGroup label="Ticket Date">
            <TextInput value={dateValue} onChange={set('ticket_date')} type="date" />
          </FieldGroup>
        </FormGrid>
        <FieldGroup label="Benefits">
          <TextInput value={form.benefits} onChange={set('benefits')} placeholder="e.g. Front row access, complimentary drink" />
        </FieldGroup>
        <FormActions onCancel={onCancel} submitLabel={initial?.id ? 'Update Ticket' : 'Create Ticket'} loading={loading} />
      </div>
    </form>
  );
};

/* ── Page ── */
const Tickets = () => {
  const [tickets, setTickets]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [modal, setModal]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast]       = useState(null);

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('tickets').select('*').order('title');
    if (error) showToast('Failed to load: ' + error.message, true);
    else setTickets(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleCreate = async (form) => {
    setSaving(true);
    const payload = { ...form };
    delete payload.id;
    const { error } = await supabase.from('tickets').insert([payload]);
    setSaving(false);
    if (error) { showToast(error.message, true); return; }
    showToast('Ticket created ✓');
    setModal(null);
    fetchTickets();
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    const { id, issued_at, ...rest } = form;
    const { error } = await supabase.from('tickets').update(rest).eq('id', id);
    setSaving(false);
    if (error) { showToast(error.message, true); return; }
    showToast('Ticket updated ✓');
    setModal(null);
    fetchTickets();
  };

  const handleDelete = async () => {
    setSaving(true);
    const { error } = await supabase.from('tickets').delete().eq('id', selected.id);
    setSaving(false);
    if (error) { showToast(error.message, true); return; }
    showToast('Ticket deleted ✓');
    setModal(null);
    fetchTickets();
  };

  const closeModal = () => { setModal(null); setSelected(null); };

  const totalRevenue = tickets.reduce((acc, t) => acc + Number(t.ticket_price || 0), 0);

  return (
    <main>
      <Sidebar />
      <div className='pagestrcutre'>
        <Topbar />

        {toast && (
          <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 99999, background: toast.isError ? '#a33' : '#2d7a2d', color: '#fff', padding: '12px 20px', borderRadius: 8, fontFamily: 'Inter', fontSize: 13, boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }}>{toast.msg}</div>
        )}

        <div className='pagetop_div'>
          <div className='pagetop_div1'>
            <h3 className='pagetop_text1'>Tickets</h3>
            <Button text='+ Create Ticket' icon={null} onClick={() => { setSelected(null); setModal('create'); }} />
          </div>
          <p className='pagetop_text2'>Manage ticket types, pricing, and availability</p>
        </div>

        <div className='analytics_div'>
          <Analytic_cards text1='Total Tickets'  text2={tickets.length}                      text3='All types' />
          <Analytic_cards text1='Total Revenue'  text2={`EGP ${totalRevenue.toLocaleString()}`} text3='Sum of ticket prices' />
          <Analytic_cards text1='Free Tickets'   text2={tickets.filter(t => Number(t.ticket_price) === 0).length} text3='No charge' />
        </div>

        <div className='page_content' style={{ paddingBottom: 30 }}>
          {loading ? (
            <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#8B8B7A' }}>Loading…</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {tickets.map(t => (
                <TicketCard key={t.id} {...t}
                  onEdit={() => { setSelected(t); setModal('edit'); }}
                  onDelete={() => { setSelected(t); setModal('delete'); }}
                />
              ))}
              {tickets.length === 0 && (
                <p style={{ fontFamily: '"Cormorant Garamond"', fontSize: 16, fontStyle: 'italic', color: 'rgba(13,13,13,0.4)', padding: 40 }}>No tickets yet. Create one!</p>
              )}
            </div>
          )}
        </div>

        <Modal open={modal === 'create'} onClose={closeModal} title="Create Ticket">
          <TicketForm onSave={handleCreate} onCancel={closeModal} loading={saving} />
        </Modal>
        <Modal open={modal === 'edit'} onClose={closeModal} title="Edit Ticket">
          <TicketForm initial={selected} onSave={handleUpdate} onCancel={closeModal} loading={saving} />
        </Modal>
        <Modal open={modal === 'delete'} onClose={closeModal} title="Delete Ticket" width={420}>
          <DeleteConfirm label={selected?.title} onConfirm={handleDelete} onCancel={closeModal} loading={saving} />
        </Modal>
      </div>
    </main>
  );
};

export default Tickets;